import type { LocationSearchPractice } from '@/react-query/useReferralInfo';

/**
 * Extracts unique specialties from location search results
 */
export const extractSpecialtiesFromResults = (
  practices: LocationSearchPractice[],
): Array<{ id: number; name: string }> => {
  if (!practices || practices.length === 0) return [];

  const specialtyMap = new Map<number, { id: number; name: string }>();

  practices.forEach((practice) => {
    practice.specialists.forEach((specialist) => {
      if (!specialtyMap.has(specialist.specialtyId)) {
        specialtyMap.set(specialist.specialtyId, {
          id: specialist.specialtyId,
          name: specialist.specialtyName,
        });
      }
    });
  });

  // Convert to array and sort by name
  return Array.from(specialtyMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
};

/**
 * Filters practices by selected specialty
 */
export const filterPracticesBySpecialty = (
  practices: LocationSearchPractice[],
  specialtyId?: number,
): LocationSearchPractice[] => {
  if (!specialtyId) return practices;

  return practices.filter((practice) =>
    practice.specialists.some(
      (specialist) => specialist.specialtyId === specialtyId,
    ),
  );
};

/**
 * Formats a practice address from its components
 */
export const formatPracticeAddress = (
  practice: Pick<LocationSearchPractice, 'address' | 'city' | 'province' | 'postalCode'>,
): string => {
  const addressParts = [
    practice.address,
    practice.city,
    practice.province,
    practice.postalCode,
  ].filter(Boolean);

  return addressParts.join(', ');
};

/**
 * Transforms specialty data to autocomplete options
 */
export const transformSpecialtiesToOptions = (
  specialties: Array<{ id: number; name: string }>,
  searchTerm: string,
) => {
  return specialties
    .filter((specialty) =>
      specialty.name.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .map((specialty) => ({
      value: specialty.id.toString(),
      label: specialty.name,
    }));
};

/**
 * Transforms practice data to autocomplete options
 */
export const transformPracticesToOptions = (
  practices: LocationSearchPractice[],
  searchTerm: string,
) => {
  return practices
    .filter((practice) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        practice.practiceName.toLowerCase().includes(searchLower) ||
        practice.address?.toLowerCase().includes(searchLower) ||
        practice.city?.toLowerCase().includes(searchLower) ||
        practice.province?.toLowerCase().includes(searchLower) ||
        practice.postalCode?.toLowerCase().includes(searchLower)
      );
    })
    .map((practice) => {
      const addressParts = formatPracticeAddress(practice);

      return {
        value: practice.id.toString(),
        label: practice.practiceName,
        secondaryLabel: `${addressParts} (${practice.distance} km away)`,
      };
    });
};

/**
 * Transforms specialist data to autocomplete options
 */
export const transformSpecialistsToOptions = (
  specialists: Array<{
    id: number;
    specialistName: string;
    specialtyName: string;
  }>,
  searchTerm: string,
) => {
  return specialists
    .filter((specialist) =>
      specialist.specialistName
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
    )
    .map((specialist) => ({
      value: specialist.id.toString(),
      label: specialist.specialistName,
      secondaryLabel: specialist.specialtyName,
    }));
};

